import React, { useState } from "react";
import { Box, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import useTranslation from "../../../../shared/hooks/useTranslation";

const dt = {
  textPrimary: "#1a1a2e",
  textSecondary: "#6B7280",
  blue: "var(--accent, #3498DB)",
  blueGlow: "var(--accent-glow, rgba(52, 152, 219, 0.12))",
  amberGlow: "rgba(245, 158, 11, 0.10)",
  amber: "#F59E0B",
  font: 'var(--font-sans)',
  radiusSm: "8px",
};

interface PricingDefaultsBannerProps {
  hasDefaults: boolean;
  loading?: boolean;
}

const PricingDefaultsBanner = ({
  hasDefaults,
  loading,
}: PricingDefaultsBannerProps) => {
  const { translate } = useTranslation(["serviceOffers"]);
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  if (dismissed || loading) return null;

  if (hasDefaults) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          background: dt.blueGlow,
          border: `1px solid ${dt.blue}20`,
          borderRadius: dt.radiusSm,
          px: 2,
          py: 1.5,
          position: "relative",
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 18, color: dt.blue, mt: 0.2, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              color: dt.textPrimary,
              fontWeight: 500,
              lineHeight: 1.5,
            }}
          >
            {translate("defaultsBannerHasDefaults")}
          </Box>
          <Box
            component="span"
            onClick={() => navigate("/settings/pricing")}
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              color: dt.blue,
              cursor: "pointer",
              fontWeight: 600,
              mt: 0.25,
              display: "inline-block",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("editDefaults")}
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          sx={{ color: dt.textSecondary, mt: -0.5, mr: -0.5 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  }

  // No defaults -- show encouragement banner
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        background: dt.amberGlow,
        border: `1px solid ${dt.amber}20`,
        borderRadius: dt.radiusSm,
        px: 2,
        py: 1.5,
        position: "relative",
      }}
    >
      <LightbulbOutlinedIcon
        sx={{ fontSize: 18, color: dt.amber, mt: 0.2, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1 }}>
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textPrimary,
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          {translate("defaultsBannerNoDefaults")}
        </Box>
        <Box
          component="span"
          onClick={() => navigate("/settings/pricing")}
          sx={{
            fontFamily: dt.font,
            fontSize: "12px",
            color: dt.blue,
            cursor: "pointer",
            fontWeight: 600,
            mt: 0.25,
            display: "inline-block",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("setUpDefaults")}
        </Box>
      </Box>
      <IconButton
        size="small"
        onClick={() => setDismissed(true)}
        sx={{ color: dt.textSecondary, mt: -0.5, mr: -0.5 }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
};

export default PricingDefaultsBanner;
